// src/components/RouteVeil.js
//
// Navy veil over client-side route changes. BrowserRouter swaps content the
// instant a link is clicked, which reads as a hard cut; the veil covers the
// content area during that frame, holds for a fixed beat long enough to mask
// the swap, then drops away in one motion — the new page settling into place
// beneath the fixed menu bar.
//
// The hold used to be longer for navigations that land on a section further
// down the page (e.g. "Back to Shop" landing on the grid), because that
// landing used to animate a ~600ms smooth-scroll into place and the veil had
// to outlast it. That scroll is now instant (see utils/smoothScroll.js —
// AppAppBar passes `instant: true` for any cross-page landing, since the
// visitor never saw the pre-scroll position, so animating toward it only
// exposed a scroll happening on a page they'd just arrived at). With no
// animation left to hide, every landing — hero or section — settles in the
// same single short hold.
//
// Why it merges seamlessly into the bar:
//   The veil covers ONLY the content area — its top edge sits on the bar's
//   bottom edge and tracks it live (a scroll listener, while the veil is up)
//   as the bar condenses. It never overlaps the bar, so the bar keeps its own
//   translucent colour throughout and there's nothing to blend. The veil
//   itself is the bar's non-condensed navy (APPBAR_NAVY_RGB), so at rest it
//   reads as the bar extended down over the page; as it drops away it
//   reveals the real page from just under the bar downward.
//
// Deliberately a FIXED hold, not settle-detection: an earlier version polled
// scroll position every animation frame to decide when to lift, with a hard
// cap meant to guarantee the cover could never hang — but that cap lived
// INSIDE the same per-frame check, so it was only as reliable as
// requestAnimationFrame itself. rAF fully pauses in a backgrounded/hidden
// tab, which let the "hard cap" balloon to several seconds in practice. A
// hold scheduled once with setTimeout can't get stuck that way: it fires on
// its own regardless of tab focus.
//
// Skipped for reduced-motion users and admin routes.
//
// Mount once inside <Router>, next to ScrollToTop.
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { EASE, APPBAR_NAVY_RGB } from '../config/atelier';

const HOLD_MS = 220; // mask the route swap + the (now instant) landing jump
const LIFT_MS = 580; // the drop, desktop
const LIFT_MS_MOBILE = 480; // ...and on phones: a shorter viewport is less
  // distance for the drop to cover, so the same duration would read as
  // travelling slower than desktop — shortened to keep the felt speed
  // consistent, the same reasoning already applied to useParallax's depth
  // and the product-tile hover zoom elsewhere in this codebase.
const FALLBACK_TOP = 64; // bar height before it's ever been measured, desktop
const FALLBACK_TOP_MOBILE = 56; // MUI's non-condensed Toolbar is 56px on
  // mobile portrait, not the 64px it is from the `sm` breakpoint up
  // (theme.mixins.toolbar) — matched here for the rare case the bar hasn't
  // mounted yet when this fallback is read.

const NAVY = `rgb(${APPBAR_NAVY_RGB})`;

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.innerWidth < 600;

const barBottomNow = () => {
  const bar = document.querySelector('header.MuiAppBar-root');
  return bar ? Math.round(bar.getBoundingClientRect().bottom) : 0;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function RouteVeil() {
  const { pathname } = useLocation();
  // 'idle' (unmounted) → 'cover' (flat navy over the content) → 'lift' (dropping away)
  const [stage, setStage] = useState('idle');
  // Top edge of the veil = the bar's bottom edge, so it never overlaps the
  // bar. Kept in sync with a scroll listener while the veil is up, so a
  // navigation that condenses/expands the bar mid-flight still lands the
  // drop flush against the bar's final height.
  const [top, setTop] = useState(() => (isMobileViewport() ? FALLBACK_TOP_MOBILE : FALLBACK_TOP));
  const prevPath = useRef(pathname);
  const isFirst = useRef(true);
  // Fixed per-navigation at cover time (not re-read mid-flight): a live
  // orientation change while the veil happens to be up is a rare enough
  // edge case that re-deriving it wouldn't be worth the extra state.
  const liftMsRef = useRef(LIFT_MS);

  // useLayoutEffect so the cover is painted in the same frame the new route
  // first renders — the old→new cut is never visible.
  useLayoutEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return undefined;
    }
    if (pathname === prevPath.current) return undefined;
    prevPath.current = pathname;
    // Admin is a utility surface; hash/state-only changes never reach here.
    if (pathname.startsWith('/admin') || prefersReducedMotion()) return undefined;

    const mobile = isMobileViewport();
    const fallbackTop = mobile ? FALLBACK_TOP_MOBILE : FALLBACK_TOP;
    liftMsRef.current = mobile ? LIFT_MS_MOBILE : LIFT_MS;

    setTop(barBottomNow() || fallbackTop);
    setStage('cover');

    const onScroll = () => setTop(barBottomNow() || fallbackTop);
    window.addEventListener('scroll', onScroll, { passive: true });

    const lift = setTimeout(() => {
      setTop(barBottomNow() || fallbackTop); // final position before the drop
      setStage('lift');
    }, HOLD_MS);
    const done = setTimeout(() => setStage('idle'), HOLD_MS + liftMsRef.current + 40);

    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(lift);
      clearTimeout(done);
    };
  }, [pathname]);

  if (stage === 'idle') return null;

  return (
    <Box
      aria-hidden="true"
      data-testid="route-veil"
      sx={{
        position: 'fixed',
        top: `${top}px`,
        left: 0,
        right: 0,
        bottom: 0,
        // Above the bar (1100) and the WhatsApp FAB (1300) so the FAB is
        // hidden during the drop; the veil starts below the bar, so a high
        // z-index never lets it paint over the bar itself.
        zIndex: 2000,
        bgcolor: NAVY,
        pointerEvents: 'none',
        transform: stage === 'lift' ? 'translateY(100%)' : 'translateY(0)',
        transition: stage === 'lift' ? `transform ${liftMsRef.current}ms ${EASE}` : 'none',
        willChange: 'transform',
      }}
    />
  );
}
